import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import useUIStore from "../../store/uiStore";
import { searchService } from "../../services/api";
import { menuItems } from "./Sidebar";
import ChangePasswordModal from "./ChangePasswordModal";

const PAGES = menuItems.flatMap((section) => section.items.map((item) => ({ ...item, section: section.section })));

const todayLabel = () =>
  new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });

// Where each kind of result opens. The destination pages read these one-shot URL parameters.
const enc = encodeURIComponent;
const PATHS = {
  customers: (i) => `/showUsers?search=${enc(i.phone || i.title)}`,
  partners: (i) => `/showVender?review=${enc(i.id)}`,
  memberships: (i) => `/showMembers?membership=${enc(i.id)}`,
  bookings: (i) => `/showUsers?search=${enc(i.phone || i.title)}`,
  sos: (i) => `/viewSOSEmergency?search=${enc(i.phone || i.title)}`,
};
const GROUP_ICON = {
  customers: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  partners: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1",
  memberships: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z",
  bookings: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  sos: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
};
const BADGE_TONE = {
  active: "bg-emerald-50 text-emerald-700", approved: "bg-emerald-50 text-emerald-700", completed: "bg-emerald-50 text-emerald-700", member: "bg-blue-50 text-blue-700",
  expired: "bg-amber-50 text-amber-700", offered: "bg-amber-50 text-amber-700", searching: "bg-amber-50 text-amber-700",
  deactivated: "bg-slate-100 text-slate-600", removed: "bg-red-50 text-red-700", rejected: "bg-red-50 text-red-700", cancelled: "bg-red-50 text-red-700", unassigned: "bg-red-50 text-red-700", disabled: "bg-red-50 text-red-700",
};
const badgeTone = (b) => BADGE_TONE[String(b).toLowerCase()] || "bg-slate-100 text-slate-600";

const Icon = ({ d, className = "h-4 w-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={d} />
  </svg>
);

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, userData } = useAuthStore();
  const { toggleSidebar, toggleSidebarCollapsed, sidebarOpen, sidebarCollapsed } = useUIStore();

  const [menuOpen, setMenuOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [remote, setRemote] = useState({ q: "", groups: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const requestId = useRef(0);

  const name = userData || "Admin";
  const initial = String(name).charAt(0).toUpperCase();
  const current = PAGES.find((p) => p.path === location.pathname);
  const q = query.trim();
  const searching = q.length >= 2;

  // Desktop collapses the sidebar to icons; on small screens the same button opens the drawer.
  const onToggle = () => {
    if (window.matchMedia("(min-width: 1024px)").matches) toggleSidebarCollapsed();
    else toggleSidebar();
  };

  // Debounced server search; stale responses are ignored.
  useEffect(() => {
    if (!searching) { setRemote({ q: "", groups: [] }); setLoading(false); setError(""); return undefined; }
    setLoading(true);
    setError("");
    const id = ++requestId.current;
    const timer = setTimeout(async () => {
      try {
        const data = await searchService.search(q, 5);
        if (id === requestId.current) setRemote({ q, groups: data.groups || [] });
      } catch (e) {
        if (id === requestId.current) { setError("Search is unavailable right now"); setRemote({ q, groups: [] }); }
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [q, searching]);

  // One flat, keyboard-navigable list: matching pages first, then record results.
  const sections = useMemo(() => {
    const lower = q.toLowerCase();
    const pages = (searching ? PAGES.filter((p) => `${p.label} ${p.section}`.toLowerCase().includes(lower)).slice(0, 4) : PAGES.slice(0, 6))
      .map((p) => ({ key: `page:${p.path}`, label: p.label, sub: p.section, icon: p.icon, path: p.path }));
    const list = [];
    if (pages.length) list.push({ key: "pages", label: searching ? "Pages" : "Go to", items: pages });
    if (searching) {
      remote.groups.forEach((g) => list.push({
        key: g.key, label: g.label,
        items: g.items.map((i) => ({ key: `${g.key}:${i.id}`, label: i.title, sub: i.subtitle, badge: i.badge, icon: GROUP_ICON[g.key], path: PATHS[g.key](i) })),
      }));
    }
    return list;
  }, [q, searching, remote]);
  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  useEffect(() => { setActive(0); }, [q, remote]);

  const go = (path) => {
    navigate(path);
    setQuery("");
    setSearchOpen(false);
    inputRef.current?.blur();
  };

  useEffect(() => {
    const outside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    const shortcut = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setSearchOpen(true);
      }
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", shortcut);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", shortcut);
    };
  }, []);

  const onSearchKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && flat[active]) go(flat[active].path);
    else if (e.key === "Escape") { setSearchOpen(false); inputRef.current?.blur(); }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    navigate("/", { replace: true });
  };

  const noRecords = searching && !loading && !error && remote.q === q && remote.groups.length === 0;
  let index = -1;

  return (
    <>
      <header className={`fixed top-0 right-0 left-0 z-30 h-16 border-b border-slate-200 bg-white ${sidebarCollapsed ? "lg:left-16" : "lg:left-56"} transition-[left] duration-300`}>
        <div className="flex h-full items-center gap-3 px-3 lg:px-5">
          <button
            type="button"
            onClick={onToggle}
            className="shrink-0 rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              {sidebarOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h16" />
              )}
            </svg>
          </button>

          <div className="hidden min-w-0 sm:block">
            <p className="truncate text-[11px] font-medium uppercase tracking-wider text-slate-400">{current?.section || "Real Motors"}</p>
            <h1 className="-mt-0.5 truncate text-[15px] font-semibold leading-tight text-slate-900">{current?.label || "Admin Panel"}</h1>
          </div>

          {/* Global search */}
          <div className="relative mx-auto w-full max-w-xl" ref={searchRef}>
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSearchOpen(true); }}
              onFocus={() => setSearchOpen(true)}
              onKeyDown={onSearchKey}
              placeholder="Search customers, partners, bookings, memberships, SOS…"
              aria-label="Global search"
              role="combobox"
              aria-expanded={searchOpen}
              aria-controls="global-search-results"
              className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-14 text-sm text-slate-800 placeholder-slate-400 transition-colors focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
            {loading ? (
              <span className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500" aria-label="Searching" />
            ) : (
              <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-400 md:block">Ctrl K</kbd>
            )}

            {searchOpen && (
              <div id="global-search-results" role="listbox" className="absolute left-0 right-0 top-11 z-50 max-h-[26rem] overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl">
                {sections.map((section) => (
                  <div key={section.key} className="py-1">
                    <p className="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{section.label}</p>
                    {section.items.map((item) => {
                      index += 1;
                      const i = index;
                      return (
                        <button
                          key={item.key}
                          type="button"
                          role="option"
                          aria-selected={i === active}
                          onMouseEnter={() => setActive(i)}
                          onClick={() => go(item.path)}
                          className={`flex w-full items-center gap-3 px-3 py-2 text-left ${i === active ? "bg-blue-50" : ""}`}
                        >
                          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${i === active ? "bg-white text-blue-600" : "bg-slate-100 text-slate-500"}`}>
                            <Icon d={item.icon} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-slate-900">{item.label}</span>
                            {item.sub ? <span className="block truncate text-xs text-slate-500">{item.sub}</span> : null}
                          </span>
                          {item.badge ? <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${badgeTone(item.badge)}`}>{item.badge}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                ))}

                {!searching ? (
                  <p className="border-t border-slate-100 px-3 py-2 text-xs text-slate-400">Type at least 2 characters to search records by name, phone, booking ID or car number.</p>
                ) : null}
                {loading && remote.q !== q ? <p className="px-3 py-3 text-sm text-slate-500">Searching…</p> : null}
                {error ? <p className="px-3 py-3 text-sm text-red-600">{error}</p> : null}
                {noRecords ? <p className="px-3 py-3 text-sm text-slate-500">No customers, partners, memberships, bookings or SOS match “{q}”.</p> : null}
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <span className="hidden whitespace-nowrap text-xs font-medium text-slate-500 xl:block">{todayLabel()}</span>
            <span className="hidden h-6 w-px bg-slate-200 xl:block" />

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Account menu"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0C2451] text-sm font-semibold text-white">{initial}</span>
                <span className="hidden text-left md:block">
                  <span className="block max-w-[9rem] truncate text-sm font-medium leading-tight text-slate-900">{name}</span>
                  <span className="block text-[11px] leading-tight text-slate-500">Administrator</span>
                </span>
                <svg className={`hidden h-3.5 w-3.5 text-slate-400 transition-transform md:block ${menuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {menuOpen && (
                <div role="menu" className="absolute right-0 top-12 z-50 w-60 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                  <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0C2451] text-base font-semibold text-white">{initial}</span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{name}</p>
                      <p className="text-xs text-slate-500">Administrator</p>
                    </div>
                  </div>
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setPasswordOpen(true); }} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50">
                    <Icon d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    Change password
                  </button>
                  <button type="button" role="menuitem" onClick={handleLogout} className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50">
                    <Icon d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      {passwordOpen ? <ChangePasswordModal onClose={() => setPasswordOpen(false)} /> : null}
    </>
  );
}
